import { useState } from 'react';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Play, Check, X } from 'lucide-react';

const VIDEOS = [
    {
        slug: 'electric_dirtbike',
        title: 'Electric Dirtbike',
        description: 'Rider performing a dramatic wheelie on a mountain trail at golden hour',
        src: '/videos/electric_dirtbike.mp4',
    },
    {
        slug: 'skateboard',
        title: 'Skateboard',
        description: 'Pro skateboarder performing a kickflip over a concrete gap at sunset',
        src: '/videos/skateboard.mp4',
    },
    {
        slug: 'electric_longboard',
        title: 'Electric Longboard',
        description: 'Rider carving downhill on a coastal road at sunset',
        src: '/videos/electric_longboard.mp4',
    },
    {
        slug: 'euc',
        title: 'Electric Unicycle (EUC)',
        description: 'High-speed lean through a futuristic neon-lit tunnel',
        src: '/videos/euc.mp4',
    },
    {
        slug: 'gas_dirtbike',
        title: 'Gas Dirtbike',
        description: 'Motocross rider launching off a massive dirt jump against dramatic sky',
        src: '/videos/gas_dirtbike.mp4',
    },
    {
        slug: 'surfer',
        title: 'Surfer',
        description: 'Surfer riding inside a massive barrel wave at a tropical beach',
        src: '/videos/surfer.mp4',
    },
];

export default function VideoPreview() {
    const [approved, setApproved] = useState({});
    const [playingVideo, setPlayingVideo] = useState(null);

    const toggleApproval = (slug) => {
        setApproved(prev => ({ ...prev, [slug]: !prev[slug] }));
    };

    const approvedCount = Object.values(approved).filter(Boolean).length;

    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <p className="label-text text-hot-pink text-glow-pink">VIDEO PREVIEW</p>
                    <h1 className="text-2xl sm:text-4xl font-black mt-1 font-display text-foreground tracking-wide">
                        Action Rider Videos
                    </h1>
                    <p className="text-muted-foreground mt-2" style={{ fontFamily: 'var(--font-body)' }}>
                        Preview the challenge completion videos. These will be used throughout the app for splash screens, challenge completions, and more.
                    </p>
                    {approvedCount > 0 && (
                        <div className="mt-3 flex items-center gap-2">
                            <div className="px-3 py-1 rounded-full bg-neon-green/15 border border-neon-green/20 text-neon-green text-sm font-display tracking-wider">
                                {approvedCount} / {VIDEOS.length} APPROVED
                            </div>
                        </div>
                    )}
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {VIDEOS.map((video, index) => (
                        <motion.div
                            key={video.slug}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <Card className={`glass-card overflow-hidden ${approved[video.slug] ? 'border-neon-green/30 box-glow' : ''}`}
                                data-testid={`video-card-${video.slug}`}
                            >
                                <div className="relative aspect-video bg-black">
                                    <video
                                        src={video.src}
                                        className="w-full h-full object-cover"
                                        controls
                                        loop
                                        muted
                                        playsInline
                                        preload="metadata"
                                        data-testid={`video-${video.slug}`}
                                    />
                                </div>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h3 className="font-bold font-display text-foreground text-sm tracking-wider uppercase">
                                                {video.title}
                                            </h3>
                                            <p className="text-xs text-muted-foreground mt-1" style={{ fontFamily: 'var(--font-body)' }}>
                                                {video.description}
                                            </p>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant={approved[video.slug] ? "default" : "outline"}
                                            className={`shrink-0 rounded-full ${
                                                approved[video.slug]
                                                    ? 'bg-neon-green text-black hover:bg-neon-green/80'
                                                    : 'border-white/10 hover:bg-white/5'
                                            }`}
                                            onClick={() => toggleApproval(video.slug)}
                                            data-testid={`approve-${video.slug}`}
                                        >
                                            {approved[video.slug] ? (
                                                <><Check className="h-4 w-4 mr-1" /> Approved</>
                                            ) : (
                                                'Approve'
                                            )}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </div>
        </Layout>
    );
}
